import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import { ConnectAccountOnboarding, ConnectComponentsProvider } from '@stripe/react-connect-js';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import {
  ArrowLeft, Zap, Users, Activity as ActivityIcon, ArrowDownLeft, Loader2, ChevronDown, Banknote, Wallet
} from 'lucide-react';

import { API_URL as BACKEND_URL } from '../config/api';
import PayoLogo from './PayoLogo';
import { getActivityRow } from '../lib/activityFormat';

const TYPE_META = {
  quick_split: { inLabel: 'Quick Split received', outLabel: 'Quick Split sent', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Zap },
  quick_split_payment: { inLabel: 'Quick Split received', outLabel: 'Quick Split paid', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: ArrowDownLeft },
  group_settlement: { inLabel: 'Settlement received', outLabel: 'Settlement sent', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Users },
  card_payment: { inLabel: 'Payment received', outLabel: 'Card payment sent', color: 'bg-gray-100 text-gray-600 border-gray-200', icon: ArrowDownLeft },
  withdrawal: { inLabel: 'Deposit', outLabel: 'Withdrawal', color: 'bg-gray-100 text-gray-600 border-gray-200', icon: ArrowDownLeft },
  cashout: { inLabel: 'Cash out', outLabel: 'Cash out to bank', color: 'bg-purple-100 text-purple-700 border-purple-200', icon: ArrowDownLeft },
};

const fmt = (cents) => {
  const dollars = Math.abs(cents) / 100;
  const sign = cents >= 0 ? '+' : '-';
  return `${sign}$${dollars.toFixed(2)}`;
};

const fmtDate = (iso) => {
  const d = new Date(iso);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const WalletPage = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const PAGE_SIZE = 20;

  const fetchTransactions = useCallback(async (skip = 0, append = false) => {
    if (skip === 0) setIsLoading(true);
    else setIsLoadingMore(true);
    try {
      const params = new URLSearchParams({ skip: String(skip), limit: String(PAGE_SIZE) });
      if (filter) params.set('type', filter);

      const [walletRes, txnRes] = await Promise.all([
        skip === 0 ? fetch(`${BACKEND_URL}/api/wallet`, { credentials: 'include' }) : null,
        fetch(`${BACKEND_URL}/api/wallet/transactions?${params}`, { credentials: 'include' }),
      ].filter(Boolean));

      if (skip === 0 && walletRes?.ok) {
        await walletRes.json(); // discard balance/totals — ledger-only system
      }
      if (txnRes.ok) {
        const data = await txnRes.json();
        setTransactions(prev => append ? [...prev, ...data.transactions] : data.transactions);
        setTotal(data.total);
      }
    } catch (err) { console.error('Wallet fetch error:', err); }
    setIsLoading(false);
    setIsLoadingMore(false);
  }, [filter]);

  useEffect(() => {
    fetchTransactions(0);
  }, [fetchTransactions]);

  const handleLoadMore = () => fetchTransactions(transactions.length, true);
  const hasMore = transactions.length < total;

  // Activity filters. Structure is intentionally modular so a future
  // "Public Splits" tab can be appended here without reworking the UI.
  const filters = [
    { key: null, label: 'All' },
    { key: 'quick_split', label: 'Quick Splits' },
  ];

  const [cashoutState, setCashoutState] = useState('idle');
  const [cashoutData, setCashoutData] = useState(null);
  const [cashoutStatus, setCashoutStatus] = useState(null);
  const [cashoutError, setCashoutError] = useState('');

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/cashout/status`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setCashoutStatus(d); })
      .catch(() => {});
  }, []);

  const handleCashOut = async () => {
    setCashoutState('loading');
    try {
      const res = await fetch(`${BACKEND_URL}/api/cashout/preview`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (data.can_cashout) { setCashoutData(data); setCashoutState('preview'); }
        else { setCashoutError(data.reason || 'Cannot cash out'); setCashoutState('error'); }
      } else { setCashoutError('Failed to load'); setCashoutState('error'); }
    } catch { setCashoutError('Connection error'); setCashoutState('error'); }
  };

  const handleConfirmCashOut = async () => {
    setCashoutState('confirming');
    try {
      const res = await fetch(`${BACKEND_URL}/api/cashout/withdraw`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        setCashoutData(await res.json());
        setCashoutState('success');
        fetchTransactions(0);
        const wRes = await fetch(`${BACKEND_URL}/api/wallet`, { credentials: 'include' });
        if (wRes.ok) { const w = await wRes.json(); setBalanceCents(w.balance_cents || 0); }
      } else {
        const err = await res.json();
        setCashoutError(typeof err.detail === 'string' ? err.detail : 'Cash out failed');
        setCashoutState('error');
      }
    } catch { setCashoutError('Connection error'); setCashoutState('error'); }
  };

  const [stripeConnectInstance, setStripeConnectInstance] = useState(null);

  const handleStartOnboarding = async () => {
    setCashoutState('loading');
    try {
      // Step 1: Create/get connected account
      const onboardRes = await fetch(`${BACKEND_URL}/api/cashout/onboard`, { method: 'POST', credentials: 'include' });
      if (!onboardRes.ok) { const err = await onboardRes.json().catch(() => ({})); setCashoutError(typeof err.detail === 'string' ? err.detail : 'Setup failed'); setCashoutState('error'); return; }
      const onboardData = await onboardRes.json();

      if (onboardData.onboarding_complete) {
        setCashoutStatus({ can_cashout: true, onboarding_complete: true });
        setCashoutState('idle');
        return;
      }

      // Step 2: Initialize Stripe Connect embedded component
      const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
      if (!publishableKey) {
        setCashoutError('Stripe configuration missing. Please contact support.');
        setCashoutState('error');
        return;
      }

      const instance = loadConnectAndInitialize({
        publishableKey,
        fetchClientSecret: async () => {
          const res = await fetch(`${BACKEND_URL}/api/cashout/account-session`, { method: 'POST', credentials: 'include' });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(typeof err.detail === 'string' ? err.detail : 'Failed to create session');
          }
          const data = await res.json();
          return data.client_secret;
        },
        appearance: { overlays: 'dialog', variables: { colorPrimary: '#16a34a' } },
      });

      setStripeConnectInstance(instance);
      setCashoutState('onboarding');
    } catch (err) {
      console.error('Onboarding error:', err);
      setCashoutError(err.message || 'Setup failed. Please try again.');
      setCashoutState('error');
    }
  };

  const handleOnboardingComplete = () => {
    setStripeConnectInstance(null);
    setCashoutStatus({ can_cashout: true, onboarding_complete: true });
    setCashoutState('idle');
  };

  const fmtC = (c) => `$${(Math.abs(c) / 100).toFixed(2)}`;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 px-6 h-16">
        <div className="max-w-7xl mx-auto h-full flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="icon" onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/')} data-testid="wallet-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <PayoLogo size="md" />
          </div>
          <div className="flex items-center space-x-2">
            <ActivityIcon className="w-5 h-5 text-blue-600" />
            <h1 className="text-base font-semibold text-gray-900">Activity</h1>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        {/* Filters */}
        <div className="flex space-x-2" data-testid="wallet-filters">
          {filters.map(f => (
            <Button
              key={f.key ?? 'all'}
              size="default"
              variant={filter === f.key ? 'default' : 'outline'}
              onClick={() => setFilter(f.key)}
              className="h-10 px-4 text-sm"
              data-testid={`wallet-filter-${f.key ?? 'all'}`}
            >
              {f.label}
            </Button>
          ))}
        </div>

        {/* Activity feed */}
        <Card data-testid="wallet-txn-list">
          <CardContent className="pt-6">
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : transactions.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8" data-testid="wallet-no-txns">
                No transactions yet
              </p>
            ) : (
              <div className="divide-y divide-gray-100">
                {transactions.map((txn, i) => {
                  const meta = TYPE_META[txn.type] || TYPE_META.withdrawal;
                  const Icon = meta.icon;
                  const { label, tag, typeTag, isIncoming } = getActivityRow(txn);
                  return (
                    <div
                      key={txn.id || i}
                      className="flex items-center gap-3 py-3 sm:py-4"
                      data-testid={`wallet-txn-${txn.id}`}
                    >
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${isIncoming ? 'bg-green-100' : 'bg-gray-100'}`}>
                        <Icon className={`w-4 h-4 ${isIncoming ? 'text-green-600' : 'text-gray-500'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{label}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <p className="text-xs text-gray-400 truncate">{fmtDate(txn.created_at)}</p>
                          {typeTag && (
                            <span
                              className="text-[10px] uppercase tracking-wide text-gray-400"
                              data-testid={`wallet-txn-type-${txn.id}`}
                            >
                              · {typeTag}
                            </span>
                          )}
                          {tag && tag.text !== 'Paid' && (
                            <span
                              className="text-[10px] uppercase tracking-wide text-gray-400"
                              data-testid={`wallet-txn-tag-${txn.id}`}
                            >
                              · {tag.text}
                            </span>
                          )}
                        </div>
                      </div>
                      <span className={`text-sm font-semibold tabular-nums whitespace-nowrap ml-2 ${isIncoming ? 'text-green-600' : 'text-gray-700'}`}>
                        {fmt(txn.amount_cents)}
                      </span>
                    </div>
                  );
                })}

                {hasMore && (
                  <Button
                    variant="ghost"
                    className="w-full mt-3 text-gray-500"
                    onClick={handleLoadMore}
                    disabled={isLoadingMore}
                    data-testid="wallet-load-more"
                  >
                    {isLoadingMore ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ChevronDown className="w-4 h-4 mr-2" />}
                    Load more
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WalletPage;
